import React, { useLayoutEffect, forwardRef } from "react";
import PropTypes from "prop-types";

import { useInputState, useInputElement, usePrevious } from "./hooks";
import {
  validateMaxLength,
  validateChildren,
  validateMaskPlaceholder
} from "./validate-props";
import { defer } from "./utils/defer";
import { isInputFocused } from "./utils/input";
import { isFunction, toString, getElementDocument } from "./utils/helpers";
import MaskUtils from "./utils/mask";
import ChildrenWrapper from "./children-wrapper";

const InputMask = forwardRef(function InputMask(props, forwardedRef) {
  const {
    alwaysShowMask,
    children,
    mask,
    maskPlaceholder,
    beforeMaskedStateChange,
    ...restProps
  } = props;

  validateMaxLength(props);
  validateMaskPlaceholder(props);

  const maskUtils = new MaskUtils({ mask, maskPlaceholder });
  const isMasked = !!mask;
  const isEditable = !restProps.disabled && !restProps.readOnly;
  const isControlled = props.value != null;
  const previousIsMasked = usePrevious(isMasked);
  const initialValue = toString(
    (isControlled ? props.value : props.defaultValue) || ""
  );

  const {
    inputRef,
    getInputState,
    setInputState,
    getLastInputState
  } = useInputState(initialValue, isMasked);
  const getInputElement = useInputElement(inputRef);

  function onChange(event) {
    const currentState = getInputState();
    const previousState = getLastInputState();
    let newInputState = maskUtils.processChange(currentState, previousState);

    if (beforeMaskedStateChange) {
      newInputState = beforeMaskedStateChange({
        currentState,
        previousState,
        nextState: newInputState
      });
    }

    setInputState(newInputState);
    props.onChange && props.onChange(event);
  }

  function onFocus(event) {
    inputRef.current = event.target;
    const currentValue = getInputState().value;

    if (isMasked && !maskUtils.isValueFilled(currentValue)) {
      let newValue = maskUtils.formatValue(currentValue);
      let newSelection = maskUtils.getDefaultSelectionForValue(newValue);
      let newInputState = { value: newValue, selection: newSelection };

      if (beforeMaskedStateChange) {
        newInputState = beforeMaskedStateChange({
          currentState: getInputState(),
          nextState: newInputState
        });
      }

      setInputState(newInputState);
      if (newValue !== currentValue && props.onChange) {
        props.onChange(event);
      }

      // Chrome resets selection after focus, so defer restoring it
      defer(() => {
        setInputState(getLastInputState());
      });
    }

    props.onFocus && props.onFocus(event);
  }

  function onBlur(event) {
    const currentValue = getInputState().value;
    const lastValue = getLastInputState().value;

    if (isMasked && !alwaysShowMask && maskUtils.isValueEmpty(lastValue)) {
      let newValue = "";
      let newInputState = { value: newValue, selection: { start: null, end: null } };

      if (beforeMaskedStateChange) {
        newInputState = beforeMaskedStateChange({
          currentState: getInputState(),
          nextState: newInputState
        });
      }

      setInputState(newInputState);
      if (newValue !== currentValue && props.onChange) {
        props.onChange(event);
      }
    }

    props.onBlur && props.onBlur(event);
  }

  function onMouseDown(event) {
    const input = getInputElement();
    const { value } = getInputState();
    const inputDocument = getElementDocument(input);

    if (!isInputFocused(input) && !maskUtils.isValueFilled(value)) {
      const mouseDownX = event.clientX;
      const mouseDownY = event.clientY;
      const mouseDownTime = Date.now();

      const mouseUpHandler = (mouseUpEvent) => {
        inputDocument.removeEventListener("mouseup", mouseUpHandler);

        if (!isInputFocused(input)) return;

        const deltaX = Math.abs(mouseUpEvent.clientX - mouseDownX);
        const deltaY = Math.abs(mouseUpEvent.clientY - mouseDownY);
        const axisDelta = Math.max(deltaX, deltaY);
        const timeDelta = Date.now() - mouseDownTime;

        if ((axisDelta <= 10 && timeDelta <= 200) || (axisDelta <= 5 && timeDelta <= 300)) {
          const lastState = getLastInputState();
          const newSelection = maskUtils.getDefaultSelectionForValue(lastState.value);
          setInputState({ ...lastState, selection: newSelection });
        }
      };

      inputDocument.addEventListener("mouseup", mouseUpHandler);
    }

    props.onMouseDown && props.onMouseDown(event);
  }

  // Controlled inputs: keep formatted value in sync
  if (isMasked && isControlled) {
    const input = getInputElement();
    const focused = input && isInputFocused(input);
    let newValue =
      focused || alwaysShowMask || props.value
        ? maskUtils.formatValue(props.value)
        : props.value;

    if (beforeMaskedStateChange) {
      newValue = beforeMaskedStateChange({
        nextState: { value: newValue, selection: { start: null, end: null } }
      }).value;
    }

    setInputState({ ...getLastInputState(), value: newValue });
  }

  // Synchronize mask and selection on layout changes
  useLayoutEffect(() => {
    if (!isMasked) return;

    const input = getInputElement();
    const focused = isInputFocused(input);
    const previousSelection = getLastInputState().selection;
    let newInputState = { ...getInputState() };

    if (!isControlled) {
      const formatted = maskUtils.formatValue(newInputState.value);
      const empty = maskUtils.isValueEmpty(formatted);
      if (!empty || focused || alwaysShowMask) {
        newInputState.value = formatted;
      } else if (empty && !focused) {
        newInputState.value = "";
      }
    }

    if (focused && previousIsMasked === false) {
      newInputState.selection = maskUtils.getDefaultSelectionForValue(newInputState.value);
    } else if (isControlled && focused && previousSelection.start != null) {
      newInputState.selection = previousSelection;
    }

    if (beforeMaskedStateChange) {
      newInputState = beforeMaskedStateChange({
        currentState: getInputState(),
        nextState: newInputState
      });
    }

    setInputState(newInputState);
  });

  const lastState = getLastInputState();
  const lastSelection = lastState.selection;
  const lastValue = lastState.value;

  // Build the props to pass to <input> or <ChildrenWrapper>
  const inputProps = {
    ...restProps,
    onFocus,
    onBlur,
    onChange: isMasked && isEditable ? onChange : props.onChange,
    onMouseDown: isMasked && isEditable ? onMouseDown : props.onMouseDown,
    ref: (node) => {
      // Assign the actual DOM node
      inputRef.current = node;
      // Forward the ref to parent
      if (isFunction(forwardedRef)) {
        forwardedRef(node);
      } else if (forwardedRef && typeof forwardedRef === "object") {
        forwardedRef.current = node;
      }
    },
    value: isMasked && isControlled ? lastValue : props.value
  };

  if (children) {
    validateChildren(props, children);
    return <ChildrenWrapper {...inputProps}>{children}</ChildrenWrapper>;
  }

  return <input {...inputProps} />;
});

InputMask.displayName = "InputMask";

InputMask.defaultProps = {
  alwaysShowMask: false,
  maskPlaceholder: "_"
};

InputMask.propTypes = {
  alwaysShowMask: PropTypes.bool,
  beforeMaskedStateChange: PropTypes.func,
  children: PropTypes.element,
  mask: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(
      PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(RegExp)])
    )
  ]),
  maskPlaceholder: PropTypes.string,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  onMouseDown: PropTypes.func
};

export default InputMask;
